import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errMsg, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Trash2, ArrowLeft, Search, Activity, Film, Info, Share2, Copy, Check, UserPlus, SwitchCamera } from "lucide-react";
import { estimateFormRating } from "@/lib/captureRating";
import ViewSwitcher from "@/components/capture/ViewSwitcher";
import RepSparkline from "@/components/capture/RepSparkline";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

// LIST view: every capture session for the coach.
export function CaptureList() {
  const [sessions, setSessions] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/capture/sessions");
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(errMsg(e));
      setSessions([]);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this capture session?")) return;
    try {
      await api.delete(`/capture/sessions/${id}`);
      toast.success("Deleted");
      setSessions((cur) => cur.filter((x) => x.id !== id));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = (sessions || []).filter((s) => {
    if (!q.trim()) return true;
    const hay = `${s.student_name || ""} ${s.notes || ""} ${s.ai_preview || ""}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  return (
    <div className="space-y-8" data-testid="capture-list-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Motion Capture</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-2">Past sessions</h1>
          <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">
            Every clip you've captured and analyzed. Tap a card to see the AI coaching breakdown.
          </p>
        </div>
        <Link to="/coach/capture/new" className="btn-primary px-3 py-2 text-xs inline-flex items-center gap-2 mt-2" data-testid="capture-new-button">
          <Camera size={14} /> New capture
        </Link>
      </div>

      <div className="flex items-center gap-2 max-w-md border-b border-white/15 focus-within:border-[#38BDF8]">
        <Search size={14} className="text-[#A3A3A3]" />
        <input
          type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by student, drill, or feedback…"
          className="bg-transparent outline-none py-2 text-sm flex-1"
          data-testid="capture-list-search"
        />
      </div>

      {sessions === null ? (
        <div className="text-sm text-[#A3A3A3]">Loading…</div>
      ) : err ? (
        <div className="card-surface p-10 text-center max-w-xl mx-auto">
          <div className="font-display text-2xl font-black uppercase tracking-tight">Couldn't load sessions</div>
          <p className="text-sm text-[#A3A3A3] mt-3">{err}</p>
          <button onClick={load} className="btn-secondary px-4 py-2 text-xs mt-6">Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="capture-list-empty">
          <div className="w-14 h-14 mx-auto border border-[#38BDF8]/50 grid place-items-center mb-4">
            <Camera size={20} className="text-[#38BDF8]" />
          </div>
          <div className="font-display text-2xl font-black uppercase tracking-tight">
            {q ? "No matches" : "No captures yet"}
          </div>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-md mx-auto">
            {q ? "Try a different search term." : "Record a 30-90s clip to see stroke-by-stroke form analysis."}
          </p>
          {!q && (
            <Link to="/coach/capture/new" className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-2 mt-6">
              <Camera size={12} /> Record a clip
            </Link>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <Link
              key={s.id}
              to={`/coach/capture/${s.id}`}
              className="card-surface p-5 block hover:border-[#38BDF8]/40 transition-colors"
              data-testid={`capture-card-${s.id}`}
            >
              <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#A3A3A3] mb-2">
                <Activity size={12} />
                <span>{s.mode || "live"}</span>
                {s.duration_seconds ? <span>· {Math.round(s.duration_seconds)}s</span> : null}
              </div>
              <div className="font-display text-lg font-black uppercase tracking-tight line-clamp-2">
                {s.notes || "Pickleball session"}
              </div>
              {s.student_name && <div className="text-[11px] text-[#38BDF8] mt-1 font-bold">{s.student_name}</div>}
              {s.ai_preview && <div className="text-xs text-white/80 mt-2 line-clamp-3">{s.ai_preview}</div>}
              <div className="text-[10px] text-[#A3A3A3] mt-3 uppercase tracking-wider">{fmt(s.created_at)}</div>
              <button
                onClick={(e) => { e.preventDefault(); remove(s.id); }}
                className="text-[10px] uppercase tracking-wider px-2 py-1 border border-white/15 hover:border-rose-400 hover:text-rose-400 inline-flex items-center gap-1 mt-3"
                data-testid={`capture-delete-${s.id}`}
              >
                <Trash2 size={10} /> Delete
              </button>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// DETAIL view: full per-session summary + structured AI analysis.
export function CaptureDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [activePlayer, setActivePlayer] = useState(0);
  // Coach-override state — declared here (not after early returns) so we
  // don't violate React's Rules of Hooks.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideVal, setOverrideVal] = useState("");
  const [overrideNote, setOverrideNote] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);
  const videoElRef = useRef(null);
  // Roster for the "tie this capture to an athlete" picker, plus share-link UI
  // state. Both live here (above the early returns) so we stay inside React's
  // Rules of Hooks.
  const [roster, setRoster] = useState([]);
  const [tieBusy, setTieBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const load = async () => {
      try {
        const { data } = await api.get(`/capture/sessions/${id}`);
        if (cancelled) return;
        setDoc(data);
        // Poll every 4s while the multi-player AI background task is still
        // running. Stops the moment status flips to "ready" or "error".
        const status = data?.ai_analysis?.status;
        if (status === "processing") {
          timer = setTimeout(load, 4000);
        }
      } catch (e) {
        if (!cancelled) setErr(errMsg(e));
      }
    };

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id]);

  // Sync override editor's local state with the latest doc whenever we
  // open the editor or the doc updates.
  useEffect(() => {
    if (!doc) return;
    setOverrideVal(doc.coach_rating != null ? String(doc.coach_rating) : "");
    setOverrideNote(doc.coach_rating_note || "");
  }, [doc?.coach_rating, doc?.coach_rating_note]);

  // Roster for the athlete picker. Loaded once — a capture can only be tied to
  // a student already on this coach's roster.
  useEffect(() => {
    api
      .get("/students")
      .then(({ data }) => setRoster(Array.isArray(data) ? data : []))
      .catch(() => setRoster([]));
  }, []);

  if (err) {
    return (
      <div className="card-surface p-10 text-center max-w-xl mx-auto" data-testid="capture-detail-error">
        <div className="font-display text-2xl font-black uppercase tracking-tight">Couldn't load session</div>
        <p className="text-sm text-[#A3A3A3] mt-3">{err}</p>
        <Link to="/coach/capture" className="btn-secondary px-4 py-2 text-xs inline-flex items-center gap-2 mt-6">
          <ArrowLeft size={12} /> Back to sessions
        </Link>
      </div>
    );
  }
  if (!doc) return <div className="text-sm text-[#A3A3A3]">Loading…</div>;

  const shareUrl = doc.share_token
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/c/${doc.share_token}`
    : "";

  /** Mint (or fetch) the public share link for this analysis. */
  const createShareLink = async () => {
    setShareBusy(true);
    try {
      const { data } = await api.post(`/capture/sessions/${doc.id}/share`);
      setDoc((cur) => ({ ...cur, share_token: data.share_token, is_public: true }));
      toast.success("Share link ready");
    } catch (e) {
      toast.error(errMsg(e) || "Couldn't create the share link");
    } finally {
      setShareBusy(false);
    }
  };

  /** Revoke it. The token is discarded server-side, so the old URL dies. */
  const revokeShareLink = async () => {
    setShareBusy(true);
    try {
      await api.delete(`/capture/sessions/${doc.id}/share`);
      setDoc((cur) => ({ ...cur, share_token: null, is_public: false }));
      setCopied(false);
      toast.success("Share link revoked — the old URL no longer works");
    } catch (e) {
      toast.error(errMsg(e) || "Couldn't revoke the link");
    } finally {
      setShareBusy(false);
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select the link and copy manually");
    }
  };

  /** Tie (or untie) this capture to an athlete. "" clears the link. */
  const tieStudent = async (value) => {
    setTieBusy(true);
    try {
      const { data } = await api.patch(`/capture/sessions/${doc.id}`, { student_id: value });
      setDoc((cur) => ({ ...cur, student_id: data.student_id, student_name: data.student_name }));
      toast.success(value ? `Tied to ${data.student_name}` : "Untied from athlete");
    } catch (e) {
      toast.error(errMsg(e) || "Couldn't update the athlete");
    } finally {
      setTieBusy(false);
    }
  };

  const sum = doc.pose_summary || {};
  const players = Array.isArray(sum.players) ? sum.players : null;
  const isMulti = !!(players && players.length);
  // When viewing a doubles capture we let the active player tab drive the
  // breakdown panes — the top-level pose_summary in multi mode is just a
  // container (it has no reps/stroke breakdown of its own).
  const activeIdx = isMulti ? Math.min(activePlayer, players.length - 1) : 0;
  const activePlayerData = isMulti ? players[activeIdx] : null;
  const _src = activePlayerData ? (activePlayerData.analysis || {}) : sum;
  const pb = _src.pickleball_stats || {};
  const strokes = pb.stroke_breakdown || {};
  const ready = pb.ready_position || {};
  const formScore = Math.round(_src.form_score ?? _src.overall_score ?? 0);
  const reps = _src.reps || [];
  const consistency = _src.consistency;
  const keyframes = _src.keyframes || {};
  const ai = activePlayerData ? (activePlayerData.ai_analysis || {}) : (doc.ai_analysis || {});
  const rollupAi = doc.ai_analysis || {};
  const hasClip = !!doc.clip_filename;
  const clipSrc = hasClip ? `${API_BASE}/capture/sessions/${doc.id}/clip` : null;
  // Form-estimated rating (DUPR-style 2.0-5.0 scale). Drill mode caps at
  // 4.5; game/match mode unlocks up to 5.0. Coach-override (if any) wins
  // visually but the estimate is still shown for context.
  const playMode = doc.play_mode || "drill";

  // Multi-view. Two camera angles on one analysis are two sessions sharing a
  // `session_group_id` (the clip route holds one file per session, so both
  // clips cannot live on the primary). `views` comes down with the session.
  const views = doc.views || [];
  const groupId = doc.session_group_id || doc.id;
  // Suggest the angle that actually adds information: the net view resolves the
  // paddle, the behind view resolves rotation, and neither substitutes for the
  // other — so default the next capture to whichever is missing.
  const currentLabel = (doc.view_label || "").toLowerCase();
  const nextAngle = currentLabel.includes("net")
    ? "baseline"
    : currentLabel.includes("base")
    ? "net"
    : "";
  const addAngleHref =
    `/coach/capture/new?group=${groupId}` +
    (doc.student_id ? `&student=${doc.student_id}` : "") +
    (nextAngle ? `&angle=${nextAngle}` : "");
  // For doubles, `_src` is the active player's analysis; coach_context lives
  // on the top-level pose_summary. Merge so the rating formula can respect
  // "coach said this was backhand drives" even in multi-player mode.
  const ratingInput = { ...(_src || {}), coach_context: sum.coach_context || _src?.coach_context };
  const formRating = estimateFormRating(ratingInput, playMode);

  const saveOverride = async () => {
    const num = parseFloat(overrideVal);
    if (Number.isNaN(num) || num < 1.0 || num > 6.0) {
      toast.error("Rating must be between 1.0 and 6.0");
      return;
    }
    setSavingOverride(true);
    try {
      const { data } = await api.patch(`/capture/sessions/${doc.id}`, {
        coach_rating: num,
        coach_rating_note: overrideNote || null,
      });
      setDoc(data);
      setOverrideOpen(false);
      toast.success("Your call is saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSavingOverride(false);
  };

  const clearOverride = async () => {
    setSavingOverride(true);
    try {
      const { data } = await api.patch(`/capture/sessions/${doc.id}`, {
        coach_rating: 0,  // not valid → use null sentinel — backend rejects 0
      });
      setDoc(data);
    } catch {
      // Just reset locally if backend rejects — UI shouldn't bake-in a value.
      setDoc({ ...doc, coach_rating: null, coach_rating_note: null });
    }
    setOverrideVal("");
    setOverrideNote("");
    setOverrideOpen(false);
    setSavingOverride(false);
  };

  // Jump the video to a specific timestamp (used by rep callouts + sparkline hover).
  const seekTo = (t) => {
    const v = videoElRef.current;
    if (!v || !Number.isFinite(t)) return;
    try { v.currentTime = Math.max(0, t); v.play().catch(() => {}); } catch { /* ignore */ }
  };

  // Score / severity colors: neon variants read on dark, saturated darker
  // variants read on light. We check the class list on <html> at call
  // time rather than useTheme() so the helper stays a plain fn.
  const isLightTheme = () =>
    typeof document !== "undefined" && document.documentElement.classList.contains("light");
  const scoreColor = (s) => {
    const light = isLightTheme();
    if (s == null) return light ? "#6B7280" : "#52525b";
    if (s >= 80) return light ? "#15803D" : "#00ff88";
    if (s >= 60) return light ? "#B45309" : "#ffab00";
    return light ? "#B91C1C" : "#ff3b30";
  };
  const severityColor = isLightTheme()
    ? { high: "#B91C1C", medium: "#B45309", low: "#0284C7" }
    : { high: "#ff3b30", medium: "#ffab00", low: "#38BDF8" };

  return (
    <div className="space-y-6" data-testid="capture-detail-page">
      <div>
        <Link to="/coach/capture" className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold inline-flex items-center gap-1" data-testid="back-sessions">
          <ArrowLeft size={11} /> All sessions
        </Link>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mt-2">
          {doc.notes || "Pickleball session"}
        </h1>
        <div className="text-[11px] text-[#A3A3A3] uppercase tracking-wider mt-3 flex flex-wrap gap-3">
          {doc.student_name ? <span data-testid="capture-detail-student">{doc.student_name}</span> : <span>No student</span>}
          <span>· {doc.mode || "live"}</span>
          {playMode !== "drill" && <span>· {playMode}</span>}
          {isMulti && <span data-testid="multi-player-badge">· Doubles · {players.length} players</span>}
          {sum.coach_context?.stroke_type && (
            <span data-testid="capture-detail-coach-stroke" className="text-[#38BDF8]">
              · Focus: {sum.coach_context.hand ? `${sum.coach_context.hand} ` : ""}{sum.coach_context.stroke_type}
            </span>
          )}
          <span>· {fmt(doc.created_at)}</span>
        </div>
      </div>

      {/* Multi-view controls. A second camera angle joins this analysis instead
          of creating a rival one, so the student keeps a single report. */}
      <div className="space-y-3" data-testid="capture-multiview">
        <ViewSwitcher
          views={views}
          linkFor={(id) => `/coach/capture/${id}`}
          testIdPrefix="coach-view"
        />
        <Link
          to={addAngleHref}
          className="inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-widest font-bold border border-[#38BDF8]/50 text-[#38BDF8] hover:bg-[#38BDF8]/10 rounded"
          data-testid="add-angle-button"
        >
          <SwitchCamera size={13} />
          {views.length > 1 ? "Add a third angle" : "Add a second camera angle"}
        </Link>
      </div>

      {/* Athlete tie + share controls.
          - Tying a capture to a student is what makes it reachable from that
            student's page (and notifies them). Previously the athlete could only
            be set at capture time, so a session recorded before picking one —
            or with the wrong one — was stranded.
          - Sharing mints a revocable public /c/{token} link, so an analysis can
            go to a roster student or to someone with no account at all. */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="card-surface p-5" data-testid="capture-tie-panel">
          <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-display font-bold flex items-center gap-1.5">
            <UserPlus size={12} /> Athlete
          </div>
          <select
            value={doc.student_id || ""}
            onChange={(e) => tieStudent(e.target.value)}
            disabled={tieBusy}
            className="mt-3 w-full bg-black/40 border border-white/15 px-3 py-2 text-sm"
            data-testid="capture-tie-select"
          >
            <option value="">— Not tied to a student —</option>
            {roster.map((s) => (
              <option key={s.user_id} value={s.user_id}>
                {s.name}
              </option>
            ))}
          </select>
          {doc.student_id ? (
            <Link
              to={`/coach/students/${doc.student_id}`}
              className="mt-2 inline-block text-[11px] text-[#38BDF8] hover:underline"
              data-testid="capture-tie-student-link"
            >
              View {doc.student_name}'s page →
            </Link>
          ) : (
            <p className="mt-2 text-[11px] text-[#A3A3A3]">
              Tie this analysis to a student to show it on their page and send them a notification.
            </p>
          )}
        </div>

        <div className="card-surface p-5" data-testid="capture-share-panel">
          <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-display font-bold flex items-center gap-1.5">
            <Share2 size={12} /> Share analysis
          </div>

          {!doc.is_public ? (
            <>
              <p className="mt-3 text-[11px] text-[#A3A3A3]">
                Creates a read-only link anyone can open — no login needed, so it works for students
                and non-students alike.
              </p>
              <button
                onClick={createShareLink}
                disabled={shareBusy}
                className="btn-primary mt-3 px-4 py-2 text-xs"
                data-testid="capture-share-create"
              >
                {shareBusy ? "Working…" : "Create share link"}
              </button>
            </>
          ) : (
            <>
              <div className="mt-3 flex items-center gap-2">
                <input
                  readOnly
                  value={shareUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 bg-black/40 border border-white/15 px-3 py-2 text-[11px] font-mono"
                  data-testid="capture-share-url"
                />
                <button
                  onClick={copyShareLink}
                  className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-1.5 flex-shrink-0"
                  data-testid="capture-share-copy"
                >
                  {copied ? <Check size={12} className="text-[#00ff88]" /> : <Copy size={12} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10px] uppercase tracking-widest text-[#38BDF8] hover:underline font-bold"
                  data-testid="capture-share-open"
                >
                  Preview →
                </a>
                <button
                  onClick={revokeShareLink}
                  disabled={shareBusy}
                  className="text-[10px] uppercase tracking-widest text-[#ff3b30] hover:underline font-bold"
                  data-testid="capture-share-revoke"
                >
                  {shareBusy ? "Working…" : "Revoke link"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Multi-player tabs: each player gets a colored pill that swaps the
          breakdown panes below. Top-level rollup summary stays visible above. */}
      {isMulti && (
        <div className="space-y-3" data-testid="player-tabs">
          {rollupAi.status === "processing" && (
            <div
              className="card-surface p-4 text-sm text-white/85 flex items-center gap-3"
              data-testid="multi-ai-processing"
            >
              <span className="inline-block h-3 w-3 rounded-full bg-[#38BDF8] animate-pulse" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold">
                  Coaching AI running
                </div>
                <div className="text-xs text-white/70 mt-0.5">
                  Analyzing all {players.length} players in the background. This page will refresh automatically when it's ready.
                </div>
              </div>
            </div>
          )}
          {rollupAi.status === "error" && (
            <div
              className="card-surface p-4 text-sm text-rose-300"
              data-testid="multi-ai-error"
            >
              AI analysis failed. The capture and per-stroke metrics were saved — try refreshing later.
            </div>
          )}
          {rollupAi.summary && rollupAi.status !== "processing" && (
            <div className="card-surface p-4 text-sm text-white/85" data-testid="multi-rollup-summary">
              <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold mb-1">
                Match overview
              </div>
              {rollupAi.summary}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {players.map((p, i) => {
              const color = p.color || "#38BDF8";
              const isActive = i === activeIdx;
              const repCount = (p.analysis || {}).rep_count ?? 0;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActivePlayer(i)}
                  data-testid={`player-tab-${i + 1}`}
                  className={`inline-flex items-center gap-2 px-3 py-2 border transition-colors ${
                    isActive ? "bg-black border-current" : "border-white/15 hover:border-white/30"
                  }`}
                  style={{ color: isActive ? color : "rgba(255,255,255,0.75)" }}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: color }}
                  />
                  <span className="text-[11px] font-display uppercase tracking-widest font-bold">
                    Player {(p.slot ?? i) + 1}
                  </span>
                  <span className="text-[10px] font-mono text-[#A3A3A3]">
                    {repCount} reps
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        {/* LEFT RAIL: stats stack */}
        <div className="space-y-4">
          <div className="card-surface p-6">
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-display font-bold">
              Form score
            </div>
            <div
              className="font-display font-black text-8xl mt-2 leading-none tabular-nums"
              style={{ color: scoreColor(formScore) }}
              data-testid="capture-form-score"
            >
              {formScore || "—"}
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
              <Stat label="Reps" value={_src.rep_count ?? reps.length ?? 0} />
              <Stat label="Duration" value={`${Math.round(activePlayerData?.duration_seconds ?? doc.duration_seconds ?? 0)}s`} />
              <Stat label="Consistency" value={consistency != null ? `${consistency}%` : "—"} />
              <Stat label="Detection" value={_src.detection_rate != null ? `${Math.round(_src.detection_rate * 100)}%` : "—"} />
            </div>

            {/* Form-estimated rating + coach override. Coach override wins
                visually when set; estimate stays small for context. */}
            {(formRating || doc.coach_rating != null) && (
              <div
                className="mt-6 border-t border-white/10 pt-4"
                data-testid="form-rating-card"
              >
                <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-display font-bold mb-2 flex items-center gap-1.5">
                  <span>
                    {doc.coach_rating != null ? "Coach's call" : "Form-estimated rating"}
                  </span>
                  <span className="group relative inline-flex">
                    <Info size={11} className="text-[#A3A3A3]" />
                    <span className="invisible group-hover:visible absolute left-4 top-0 z-10 w-72 bg-black border border-white/15 p-2 text-[10px] text-white/85 normal-case tracking-normal font-normal">
                      Conservative estimate from this clip's mechanics. Drill mode caps at 4.5; game/match mode caps at 5.0. Coach can override at any time — overrides are persisted with the session and used to anchor future calibration.
                    </span>
                  </span>
                  {playMode !== "drill" && (
                    <span
                      className="ml-1 text-[9px] text-[#38BDF8] border border-[#38BDF8]/40 px-1.5 py-0.5 normal-case tracking-normal"
                      data-testid="play-mode-badge"
                    >
                      {playMode}
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-2">
                  <div
                    className="font-display font-black text-5xl tabular-nums leading-none"
                    style={{
                      color: doc.coach_rating != null
                        ? "#38BDF8"
                        : scoreColor((formRating?.rating ?? 0) * 20),
                    }}
                    data-testid="form-rating-value"
                  >
                    {doc.coach_rating != null
                      ? doc.coach_rating.toFixed(1)
                      : `~${formRating.rating.toFixed(1)}`}
                  </div>
                  {doc.coach_rating == null && formRating && (
                    <div className="text-[11px] text-[#A3A3A3] font-mono">
                      ± {formRating.band.toFixed(1)}
                    </div>
                  )}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-[#A3A3A3] font-display font-bold">
                  {doc.coach_rating != null ? (
                    <>
                      coach override
                      {doc.coach_rating_set_at && (
                        <span> · {new Date(doc.coach_rating_set_at).toLocaleDateString()}</span>
                      )}
                      {formRating && (
                        <span> · model said ~{formRating.rating.toFixed(1)}</span>
                      )}
                    </>
                  ) : (
                    <>
                      {formRating.confidence} confidence · {formRating.reps} reps
                      {formRating.components?.stroke_types != null && (
                        <span> · {formRating.components.stroke_types} stroke type{formRating.components.stroke_types === 1 ? "" : "s"}</span>
                      )}
                    </>
                  )}
                </div>
                {doc.coach_rating != null && doc.coach_rating_note && (
                  <div className="mt-2 text-[11px] text-white/75 italic" data-testid="coach-rating-note">
                    “{doc.coach_rating_note}”
                  </div>
                )}
                {doc.coach_rating == null && formRating?.context && (
                  <div
                    className="mt-2 text-[11px] text-white/75 border-l-2 border-[#38BDF8]/60 pl-2 leading-relaxed"
                    data-testid="form-rating-context"
                  >
                    {formRating.context}
                  </div>
                )}

                {/* Override controls — inline editor that opens on click. */}
                {!overrideOpen && (
                  <button
                    onClick={() => {
                      setOverrideOpen(true);
                      if (!overrideVal && formRating) setOverrideVal(formRating.rating.toFixed(1));
                    }}
                    className="mt-3 text-[10px] uppercase tracking-widest text-[#38BDF8] hover:underline font-bold"
                    data-testid="rating-override-open"
                  >
                    {doc.coach_rating != null ? "Edit override →" : "Override rating →"}
                  </button>
                )}
                {overrideOpen && (
                  <div className="mt-3 space-y-2 border border-[#38BDF8]/30 bg-[#38BDF8]/5 p-3" data-testid="rating-override-form">
                    <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold">
                      Your call (1.0 – 6.0)
                    </div>
                    <input
                      type="number"
                      min="1.0"
                      max="6.0"
                      step="0.1"
                      value={overrideVal}
                      onChange={(e) => setOverrideVal(e.target.value)}
                      className="w-full bg-black/30 border border-white/15 px-2 py-1.5 text-2xl font-display font-black text-[#38BDF8] tabular-nums focus:outline-none focus:border-[#38BDF8]"
                      data-testid="rating-override-input"
                    />
                    <input
                      type="text"
                      placeholder="Why? (optional — helps calibration)"
                      value={overrideNote}
                      maxLength={200}
                      onChange={(e) => setOverrideNote(e.target.value)}
                      className="w-full bg-transparent border-b border-white/15 focus:border-[#38BDF8] outline-none py-1 text-xs"
                      data-testid="rating-override-note"
                    />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex gap-2">
                        <button
                          onClick={saveOverride}
                          disabled={savingOverride}
                          className="btn-primary px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold"
                          data-testid="rating-override-save"
                        >
                          {savingOverride ? "Saving…" : "Save"}
                        </button>
                        <button
                          onClick={() => setOverrideOpen(false)}
                          className="text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-white font-bold"
                          data-testid="rating-override-cancel"
                        >
                          Cancel
                        </button>
                      </div>
                      {doc.coach_rating != null && (
                        <button
                          onClick={clearOverride}
                          disabled={savingOverride}
                          className="text-[10px] uppercase tracking-widest text-red-400 hover:text-red-300 font-bold"
                          data-testid="rating-override-clear"
                        >
                          Clear override
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-3 text-[10px] text-[#A3A3A3] leading-relaxed">
                  Conservative video-form signal — drill caps at 4.5, game at 5.0. A clip alone can't show tactical play, court coverage, or point selection. Match-played DUPR is the source of record.
                </div>
              </div>
            )}

            {/* Per-rep sparkline */}
            {reps.length > 0 && (
              <div className="mt-6 border-t border-white/10 pt-4">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-display font-bold mb-2">
                  Per-rep score
                </div>
                <RepSparkline reps={reps} clipSrc={clipSrc} onSeek={seekTo} />
              </div>
            )}

            {/* Stroke breakdown */}
            {Object.keys(strokes).length > 0 && (
              <div className="mt-6 border-t border-white/10 pt-4">
                <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-display font-bold mb-2">
                  Stroke breakdown
                </div>
                <div className="space-y-1.5">
                  {Object.entries(strokes).map(([type, data]) => (
                    <div key={type} className="flex items-center gap-3 text-sm" data-testid={`stroke-row-${type}`}>
                      <span className="font-display uppercase tracking-widest text-xs font-bold text-white w-16 flex-shrink-0">
                        {type}
                      </span>
                      <span className="text-[#A3A3A3] font-mono text-xs w-10 text-center">
                        {data.count}×
                      </span>
                      <div className="flex-1 h-2 bg-white/5 relative overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 transition-all"
                          style={{ width: `${data.avg_score || 0}%`, background: scoreColor(data.avg_score) }}
                        />
                      </div>
                      <span className="text-xs font-mono text-white w-8 text-right">{data.avg_score || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ready position */}
            {ready && ready.ready_score != null && (
              <div className="mt-6 border-t border-white/10 pt-4" data-testid="capture-ready-position">
                <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-display font-bold mb-2">
                  Ready position · between shots
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <ReadyCell value={ready.ready_score} color={scoreColor(ready.ready_score)} label="Ready score" />
                  <ReadyCell value={`${ready.paddle_up_pct ?? "—"}%`} label="Paddle up" />
                  <ReadyCell value={ready.knee_bend_avg ? `${ready.knee_bend_avg}°` : "—"} label="Knee bend" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT RAIL: video clip + AI analysis + evidence */}
        <div className="space-y-6">
          {/* Saved clip player — only when we have an uploaded clip */}
          {clipSrc && (
            <section className="card-surface p-3" data-testid="capture-clip-player">
              <div className="bg-black flex items-center justify-center" style={{ maxHeight: "75vh", overflow: "hidden" }}>
                <video
                  ref={videoElRef}
                  src={clipSrc}
                  controls
                  preload="metadata"
                  playsInline
                  className="max-h-[75vh] w-auto max-w-full"
                />
              </div>
              <div className="flex items-center justify-between mt-2 px-1">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] flex items-center gap-1">
                  <Film size={11} /> Saved clip
                </div>
                <a
                  href={clipSrc}
                  download={`pbcoach-capture-${doc.id}.${(doc.clip_mime || "").includes("mp4") ? "mp4" : "webm"}`}
                  className="text-[10px] uppercase tracking-widest text-[#38BDF8] hover:underline"
                  data-testid="capture-clip-download"
                >Download</a>
              </div>
            </section>
          )}

          {/* AI summary */}
          {ai.summary && (
            <section className="card-surface p-6" data-testid="ai-summary">
              <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-display font-bold">AI analysis</div>
              <h2 className="mt-1 font-display font-black uppercase tracking-tight text-2xl">Biomechanics report</h2>
              <p className="mt-4 text-sm text-white/85 leading-relaxed whitespace-pre-line">{ai.summary}</p>
              {ai.elite_comparison && (
                <div className="mt-5 border-l-2 border-[#38BDF8] pl-4 text-sm text-white/75 italic">
                  {ai.elite_comparison}
                </div>
              )}
              {ai.next_focus && (
                <div className="mt-5 bg-black/40 border border-white/10 p-4" data-testid="ai-next-focus">
                  <div className="text-[10px] uppercase tracking-widest text-[#ffab00] font-display font-bold">Next focus</div>
                  <p className="mt-1 text-sm">{ai.next_focus}</p>
                </div>
              )}
            </section>
          )}

          {/* Visual evidence — best vs worst rep keyframes */}
          {(keyframes.best_rep || keyframes.worst_rep) && (
            <section className="card-surface p-6" data-testid="visual-evidence">
              <h3 className="font-display font-bold uppercase tracking-tight text-lg">Visual evidence</h3>
              <p className="text-xs text-[#A3A3A3] mt-1">Skeleton frame at the apex of your best vs worst rep.</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                {keyframes.best_rep && <KeyframeCard label="Best rep" accent="#00ff88" data={keyframes.best_rep} />}
                {keyframes.worst_rep && <KeyframeCard label="Worst rep" accent="#ff3b30" data={keyframes.worst_rep} />}
              </div>
            </section>
          )}

          {/* Rep callouts */}
          {ai.rep_callouts && ai.rep_callouts.length > 0 && (
            <section className="card-surface p-6" data-testid="rep-callouts">
              <h3 className="font-display font-bold uppercase tracking-tight text-lg">Rep callouts</h3>
              <ul className="mt-3 space-y-2">
                {ai.rep_callouts.map((c, i) => {
                  // Match the callout's rep_index to a real rep so we can seek the video.
                  const target = reps.find((r) => (r.index ?? -1) === c.rep_index);
                  const t = target?.time_s;
                  return (
                    <li key={i} className="flex items-start gap-3 text-sm" data-testid={`rep-callout-${i}`}>
                      <button
                        type="button"
                        onClick={() => t != null && seekTo(t)}
                        disabled={t == null || !clipSrc}
                        className={`font-display font-black w-12 text-right ${clipSrc && t != null ? "hover:underline" : ""}`}
                        style={{ color: c.label === "best" ? "#00ff88" : c.label === "worst" ? "#ff3b30" : "#ffab00" }}
                        title={t != null && clipSrc ? `Jump to ${t.toFixed(1)}s` : ""}
                      >
                        #{c.rep_index}
                      </button>
                      <span className="text-white/85 flex-1">
                        <span className="text-[10px] uppercase tracking-widest font-display font-bold text-[#A3A3A3] mr-2">
                          {c.label}
                        </span>
                        {c.comment}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Strengths */}
          {ai.strengths && ai.strengths.length > 0 && (
            <section className="card-surface p-6" data-testid="strengths">
              <h3 className="font-display font-bold uppercase tracking-tight text-lg">Strengths</h3>
              <ul className="mt-3 space-y-2">
                {ai.strengths.map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="text-[#00ff88] font-mono">+</span>
                    <span className="text-white/85">{s}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Improvements (with severity coloring) */}
          {ai.improvements && ai.improvements.length > 0 && (
            <section className="card-surface p-6" data-testid="improvements">
              <h3 className="font-display font-bold uppercase tracking-tight text-lg">Improvements</h3>
              <div className="mt-3 space-y-3">
                {ai.improvements.map((it, i) => (
                  <div
                    key={i}
                    data-testid={`improvement-${i}`}
                    className="border border-white/5 p-4"
                    style={{ borderLeftColor: severityColor[it.severity] || "#52525b", borderLeftWidth: 3 }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-display font-bold uppercase tracking-tight">{it.area}</span>
                      <span
                        className="text-[10px] uppercase tracking-widest font-display font-bold"
                        style={{ color: severityColor[it.severity] || "#a1a1aa" }}
                      >
                        {it.severity}
                      </span>
                    </div>
                    <p className="text-sm text-[#A3A3A3] mt-1">{it.issue}</p>
                    <p className="text-sm text-white mt-2">
                      <span className="text-[#00ff88] font-mono">→ </span>
                      {it.fix}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Empty-state fallback */}
          {!ai.summary && !ai.improvements?.length && !ai.strengths?.length && (
            <div className="card-surface p-8 text-center text-sm text-[#A3A3A3]">
              No AI analysis generated for this session yet — usually a sign Mediapipe couldn't see enough of the body (try better lighting / framing) or the LLM key isn't configured.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-display font-bold">{label}</div>
      <div className="font-display font-bold mt-1">{value}</div>
    </div>
  );
}

function ReadyCell({ value, color, label }) {
  return (
    <div>
      <div className="font-display font-black text-3xl" style={{ color: color || "#fff" }}>{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-[#A3A3A3] mt-1">{label}</div>
    </div>
  );
}

function KeyframeCard({ label, accent, data }) {
  return (
    <div className="border border-white/10 bg-black/40">
      {data.image && (
        <img
          src={data.image}
          alt={label}
          data-testid={`keyframe-${label.toLowerCase().replace(/\s/g, "-")}`}
          className="w-full max-h-80 object-contain bg-black"
        />
      )}
      <div className="p-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-display font-bold" style={{ color: accent }}>
            {label}
          </div>
          <div className="text-xs text-[#A3A3A3] font-mono mt-0.5">
            t = {typeof data.time_s === "number" ? data.time_s.toFixed(1) : data.time_s}s
          </div>
        </div>
        <div className="font-display font-black text-3xl" style={{ color: accent }}>
          {data.score ?? "—"}
        </div>
      </div>
    </div>
  );
}
